import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import ReactPixel from 'react-facebook-pixel';
import Navbar from './Navbar';

interface FormData {
  name: string;
  email: string;
  phone: string;
  location: string;
  hearAboutUs: string;
  services: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    location: '',
    hearAboutUs: '',
    services: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // EmailJS configuration - you'll need to set up an EmailJS account and get these values
      const serviceId = 'service_d4dp1s8'; // Replace with your EmailJS service ID
      const templateId = 'ericka_template'; // Replace with your EmailJS template ID
      const publicKey = '70usdUghg-u3MqFCE'; // Replace with your EmailJS public key

      // Template parameters for EmailJS
      const templateParams = {
        name: formData.name,
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        location: formData.location,
        hear_about_us: formData.hearAboutUs,
        services: formData.services,
        to_email: 'rod.tabalba@gmail.com,erickatabalba@gmail.com'
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      // Track Lead Event
      ReactPixel.track('Lead');

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        location: '',
        hearAboutUs: '',
        services: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-soft-cream">
      <Navbar />
      <div className="py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 pt-24">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-deep-teal mb-4 sm:mb-6">
              Let's Create Something Beautiful Together
            </h1>
            <p className="text-lg sm:text-xl text-gray-700 max-w-2xl mx-auto leading-relaxed">
              Ready to start planning your perfect bridal look? I'd love to hear about your vision
              and help make your Kauai wedding day absolutely stunning.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8">
              <h2 className="font-serif text-2xl sm:text-3xl text-deep-teal mb-6">
                Tell Me About Your Vision
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunset-coral focus:border-transparent transition-all"
                    placeholder="Your full name"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunset-coral focus:border-transparent transition-all"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunset-coral focus:border-transparent transition-all"
                    placeholder="(808) 123-4567"
                  />
                </div>

                {/* Location */}
                <div>
                  <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                    Wedding Location *
                  </label>
                  <select
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunset-coral focus:border-transparent transition-all"
                  >
                    <option value="">Select your wedding location</option>
                    <option value="Kauai">Kauai</option>
                    <option value="Oahu">Oahu</option>
                    <option value="Maui">Maui</option>
                    <option value="Big Island">Big Island</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                {/* How did you hear about us */}
                <div>
                  <label htmlFor="hearAboutUs" className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about us? *
                  </label>
                  <input
                    type="text"
                    id="hearAboutUs"
                    name="hearAboutUs"
                    required
                    value={formData.hearAboutUs}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunset-coral focus:border-transparent transition-all"
                    placeholder="Instagram, Google, referral, etc."
                  />
                </div>

                {/* Services */}
                <div>
                  <label htmlFor="services" className="block text-sm font-medium text-gray-700 mb-2">
                    Tell me more about what type of services you're looking for *
                  </label>
                  <textarea
                    id="services"
                    name="services"
                    required
                    rows={4}
                    value={formData.services}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sunset-coral focus:border-transparent transition-all resize-vertical"
                    placeholder="Please describe your vision, what services you need (hair, makeup, both), your wedding date, number of people, and any specific requests or concerns..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-sunset-coral hover:bg-opacity-90 text-white font-semibold py-4 px-6 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {isSubmitting ? 'Sending...' : 'Send My Inquiry'}
                </button>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-800 text-sm">
                      Thank you! Your inquiry has been sent successfully. Ericka will get back to you within 24 hours!
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-800 text-sm">
                      There was an error sending your message. Please try calling 808-652-0369 directly or try submitting again.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Info & Additional Details */}
            <div className="space-y-6">
              {/* Phone Contact */}
              <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8">
                <h3 className="font-serif text-2xl text-deep-teal mb-4">
                  Prefer to Call?
                </h3>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="bg-sunset-coral text-white p-3 rounded-full">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">Call or text me directly</p>
                    <a
                      href="tel:808-652-0369"
                      className="text-2xl font-semibold text-deep-teal hover:text-sunset-coral transition-colors"
                    >
                      808-652-0369
                    </a>
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  I'm usually available 9 AM - 7 PM HST, but feel free to leave a message anytime!
                </p>
              </div>

              {/* What to Expect */}
              <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8">
                <h3 className="font-serif text-2xl text-deep-teal mb-4">
                  What Happens Next?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-island-sage text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mt-1">
                      1
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Quick Response</h4>
                      <p className="text-gray-600 text-sm">I'll get back to you within 24 hours (usually much sooner!)</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-island-sage text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mt-1">
                      2
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Complimentary Consultation</h4>
                      <p className="text-gray-600 text-sm">We'll schedule a call to discuss your vision and needs</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="bg-island-sage text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold mt-1">
                      3
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">Custom Proposal</h4>
                      <p className="text-gray-600 text-sm">I'll create a personalized package just for your special day</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Areas */}
              <div className="bg-white rounded-lg shadow-xl p-6 sm:p-8">
                <h3 className="font-serif text-2xl text-deep-teal mb-4">
                  Service Areas
                </h3>
                <p className="text-gray-600 text-sm mb-3">
                  Based on Kauai, but available for destination weddings throughout Hawaii:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-sunset-coral rounded-full"></div>
                    <span>Kauai (home base)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-sunset-coral rounded-full"></div>
                    <span>Oahu</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-sunset-coral rounded-full"></div>
                    <span>Maui</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-sunset-coral rounded-full"></div>
                    <span>Big Island</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
